import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArtworkViewer, DecalSpawner } from '../components';
import { GalleryItem } from '../types/gallery';
import { getOptimizedUrl, getCachedGalleryItems, fetchGalleryItems } from '../utils/utils';
import { scrollToTopInstant } from '../utils/scrollRestoration';

function findMatchingArtwork(gallery: GalleryItem[], targetId: string): GalleryItem | null {
  const rawId = targetId;
  const decodedId = decodeURIComponent(targetId);
  const decodedLower = decodedId.toLowerCase();

  return (
    gallery.find((item) => {
      return (
        item.id === rawId ||
        item.id === decodedId ||
        item.id.toLowerCase() === decodedLower ||
        encodeURIComponent(item.id) === rawId ||
        (rawId === 'whippin' && item.id.includes('whippin')) ||
        (rawId === 'ctrldel' && item.id.includes('ctrl'))
      );
    }) || null
  );
}

export const ViewerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const cached = getCachedGalleryItems();
  const initialMatch = (cached && id) ? findMatchingArtwork(cached, id) : null;

  const [artwork, setArtwork] = useState<GalleryItem | null>(() => initialMatch);
  const [isLoading, setIsLoading] = useState(() => !initialMatch);

  // Guarantee scroll is strictly at the top when entering viewer so "Back to Exhibition" is fully visible
  useLayoutEffect(() => {
    scrollToTopInstant();
  }, [id]);

  useEffect(() => {
    let isMounted = true;
    if (!id) return;

    if (artwork) {
      let title = artwork.id.replace(/-qm/g, '?').replace(/-c/g, ':');
      if (title.includes('untitled')) title = '[untitled]';
      document.title = title;
    }

    if (!cached || cached.length === 0) {
      fetchGalleryItems()
        .then((gallery: GalleryItem[]) => {
          if (!isMounted) return;
          const match = findMatchingArtwork(gallery, id);
          if (match) {
            setArtwork(match);
            let title = match.id.replace(/-qm/g, '?').replace(/-c/g, ':');
            if (title.includes('untitled')) title = '[untitled]';
            document.title = title;
          }
          setIsLoading(false);
          scrollToTopInstant();
        })
        .catch((err) => {
          console.error('Failed to load artwork for viewer:', err);
          if (isMounted) setIsLoading(false);
        });
    } else if (!artwork) {
      const match = findMatchingArtwork(cached, id);
      if (match) {
        setArtwork(match);
        let title = match.id.replace(/-qm/g, '?').replace(/-c/g, ':');
        if (title.includes('untitled')) title = '[untitled]';
        document.title = title;
      }
      setIsLoading(false);
      scrollToTopInstant();
    }

    return () => {
      isMounted = false;
    };
  }, [id, cached, artwork]);

  if (isLoading) {
    return (
      <div className="viewer-body" style={{ minHeight: '100vh', position: 'relative', overflow: 'visible' }}>
        <DecalSpawner rowCount={2} craziness={0.1} />
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="viewer-body" style={{ minHeight: '100vh', position: 'relative', textAlign: 'center', paddingTop: '15vh' }}>
        <DecalSpawner rowCount={2} craziness={0.1} />
        <h2 style={{ color: '#af1d24', fontFamily: 'Times, serif', fontSize: '2rem' }}>Artwork Not Found</h2>
        <p style={{ color: '#888', marginTop: '1rem' }}>No archived piece matching "{id}"</p>
        <div id="return" style={{ marginTop: '2rem' }}>
          <Link to="/" state={{ fromReturnLink: true }}>❮ Back to Exhibition</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="viewer-body" style={{ minHeight: '100vh', position: 'relative', overflow: 'visible' }}>
      <DecalSpawner rowCount={2} craziness={0.1} />
      <ArtworkViewer artwork={artwork} getOptimizedUrl={getOptimizedUrl} />
    </div>
  );
};

export default ViewerPage;
